/* ============================================================================
 * vp-lightbox.js — shared lightbox custom element for the RESTORE180 PDP.
 *
 * One <vp-lightbox> host is rendered globally (snippets/vp-lightbox.liquid via
 * layout/theme.liquid). Any element on the page opens it through data attrs —
 * sections never instantiate their own lightbox.
 *
 * Triggers (delegated, click-activated):
 *   Video:   <button data-vp-lightbox="video" data-video-url="<mp4|youtube|vimeo>">
 *   Image:   <a      data-vp-lightbox="image" data-image-src="..." [data-image-alt="..."]>
 *   Content: <button data-vp-lightbox="content" data-content-target="#template-id">
 *
 * Group navigation (Swiper gallery):
 *   Add data-vp-lightbox-group="<name>" to multiple triggers.
 *   When 2+ triggers share a group name, clicking any opens a Swiper gallery
 *   showing all items in the group. Supports image, video, and content slides.
 *   Images and videos support touch swipe; content slides disable swipe to
 *   allow internal scroll, use arrow buttons instead.
 *
 * Chain (inside content markup): close current, open a video:
 *   <button data-vp-lightbox-chain="video" data-video-url="...">
 *
 * Close: X button, click outside the dialog, or Esc.
 * Reuses the theme's trapFocus / removeTrapFocus / pauseAllMedia globals when
 * present (assets/global.js), with safe fallbacks.
 * ========================================================================== */

(function () {
  'use strict';

  var YT_RE = /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/i;
  var VIMEO_RE = /vimeo\.com\/(?:video\/)?(\d+)/i;

  function getFocusable(container) {
    return Array.prototype.slice.call(
      container.querySelectorAll(
        'a[href], button:not([disabled]), textarea, input, select, details, [tabindex]:not([tabindex="-1"])'
      )
    ).filter(function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  }

  function youTubeEmbed(id) {
    return (
      'https://www.youtube.com/embed/' +
      id +
      '?autoplay=1&rel=0&playsinline=1&modestbranding=1'
    );
  }

  function vimeoEmbed(id) {
    return 'https://player.vimeo.com/video/' + id + '?autoplay=1&playsinline=1';
  }

  class VpLightbox extends HTMLElement {
    constructor() {
      super();
      this.dialog = null;
      this.closeBtn = null;
      this.panels = {};
      this.mediaHost = null;
      this.imageEl = null;
      this.contentHost = null;
      this.openedBy = null;
      this.activeMode = null;
      this._swiper = null;
      this._swiperEl = null;
      this._navPrev = null;
      this._navNext = null;
    }

    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.dialog = this.querySelector('[data-vp-lightbox-dialog]');
      this.closeBtn = this.querySelector('[data-vp-lightbox-close]');
      this.mediaHost = this.querySelector('[data-vp-lightbox-media]');
      this.imageEl = this.querySelector('[data-vp-lightbox-image]');
      this.contentHost = this.querySelector('[data-vp-lightbox-content]');
      this.panels = {
        video: this.querySelector('.vp-lightbox__panel--video'),
        image: this.querySelector('.vp-lightbox__panel--image'),
        content: this.querySelector('.vp-lightbox__panel--content'),
      };

      // Close interactions
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', this.close.bind(this));
      }
      // Outside click (overlay area = the host itself or the transparent dialog)
      this.addEventListener('mousedown', (e) => {
        this._downOnOverlay = (e.target === this || e.target === this.dialog);
      });
      this.addEventListener('click', (e) => {
        if ((e.target === this || e.target === this.dialog) && this._downOnOverlay) this.close();
      });
      // Esc
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' || e.code === 'Escape') this.close();
      });

      // Global delegated trigger handling (one listener for whole page)
      if (!VpLightbox._delegated) {
        VpLightbox._delegated = true;
        document.addEventListener('click', VpLightbox.onDocumentClick);
      }
    }

    static get instance() {
      return document.querySelector('vp-lightbox');
    }

    static onDocumentClick(event) {
      var trigger = event.target.closest(
        '[data-vp-lightbox], [data-vp-lightbox-chain]'
      );
      if (!trigger) return;

      // RESTORE180: Disable image zoom (lightbox) on mobile for product gallery
      if (
        window.innerWidth < 750 &&
        trigger.getAttribute('data-vp-lightbox') === 'image' &&
        trigger.closest('.product__media-list')
      ) {
        event.preventDefault();
        return;
      }

      var lb = VpLightbox.instance;
      if (!lb) return;

      // Chain: close current lightbox, then open the chained target
      if (trigger.hasAttribute('data-vp-lightbox-chain')) {
        event.preventDefault();
        var chainMode = trigger.getAttribute('data-vp-lightbox-chain');
        var data = VpLightbox.readTrigger(trigger, chainMode);
        lb.close();
        // open after the close so focus/scroll-lock settle cleanly
        window.requestAnimationFrame(function () {
          lb.open(chainMode, data, trigger);
        });
        return;
      }

      var mode = trigger.getAttribute('data-vp-lightbox');
      if (!mode) return;
      event.preventDefault();
      lb.open(mode, VpLightbox.readTrigger(trigger, mode), trigger);
    }

    static readTrigger(trigger, mode) {
      if (mode === 'video') {
        return {
          url: trigger.getAttribute('data-video-url'),
          ratio: trigger.getAttribute('data-video-ratio') || '16/9',
        };
      }
      if (mode === 'image') {
        return {
          src: trigger.getAttribute('data-image-src'),
          alt: trigger.getAttribute('data-image-alt') || '',
        };
      }
      if (mode === 'content') {
        return { target: trigger.getAttribute('data-content-target') };
      }
      return {};
    }

    open(mode, data, opener) {
      if (!mode) return;

      // Group mode: 2+ triggers share a group name → Swiper gallery
      var group = opener && opener.getAttribute('data-vp-lightbox-group');
      if (group) {
        var allTriggers = Array.from(
          document.querySelectorAll('[data-vp-lightbox-group="' + group + '"]')
        );
        if (allTriggers.length > 1) {
          if (this.activeMode) this.clearActivePanel();
          this.openedBy = opener || document.activeElement;
          this.activeMode = 'group';
          var startIndex = allTriggers.indexOf(opener);
          // Show first so the element is in the layout (display:flex) before
          // Swiper init — when Swiper JS is cached it runs synchronously and
          // getBoundingClientRect() returns 0 inside display:none.
          this._showLightbox();
          this._openWithSwiper(allTriggers, startIndex < 0 ? 0 : startIndex);
          return;
        }
      }

      // Single item
      if (!this.panels[mode]) return;
      if (this.activeMode) this.clearActivePanel();
      this.openedBy = opener || document.activeElement;
      this.activeMode = mode;
      if (mode === 'video') this.renderVideo(data.url, data.ratio);
      else if (mode === 'image') this.renderImage(data.src, data.alt);
      else if (mode === 'content') this.renderContent(data.target);
      this.showPanel(mode);
      this._showLightbox();
    }

    _showLightbox() {
      this.lockScroll();
      this.setAttribute('open', '');
      this.setAttribute('aria-hidden', 'false');
      var focusTarget = this.closeBtn || this.dialog || this;
      if (typeof window.trapFocus === 'function') {
        try {
          window.trapFocus(this, focusTarget);
        } catch (e) {
          focusTarget.focus();
        }
      } else {
        this._localTrap();
        focusTarget.focus();
      }
    }

    /* ---- Swiper group mode ---- */

    _openWithSwiper(triggers, startIndex) {
      var self = this;

      // Swiper root — appended directly to dialog (no extra wrapper).
      // Initial width fills dialog up to --lb-max-w; JS trims it after init.
      var swiperEl = document.createElement('div');
      swiperEl.className = 'vp-lightbox__swiper swiper';
      swiperEl.style.width = 'min(var(--lb-max-w), 100%)';
      this._swiperEl = swiperEl;

      var wrapper = document.createElement('div');
      wrapper.className = 'swiper-wrapper';

      triggers.forEach(function (trigger, idx) {
        var mode = trigger.getAttribute('data-vp-lightbox');
        var slide = document.createElement('div');
        slide.className = 'swiper-slide vp-lightbox__slide vp-lightbox__slide--' + mode;
        slide.dataset.mode = mode;

        if (mode === 'image') {
          var img = document.createElement('img');
          img.className = 'vp-lightbox__image';
          img.src = trigger.getAttribute('data-image-src') || '';
          img.alt = trigger.getAttribute('data-image-alt') || '';
          img.loading = idx === startIndex ? 'eager' : 'lazy';

          if (img.complete && img.naturalHeight > 0) {
            // Already in browser cache — show immediately, no spinner needed
          } else {
            // Not yet loaded — show spinner, fade in when ready
            slide.classList.add('vp-lb-img--loading');
            img.style.cssText = 'opacity:0;transition:opacity 0.25s ease;';
            var onImgLoad = function () {
              slide.classList.remove('vp-lb-img--loading');
              img.style.opacity = '1';
              // Re-measure now that the image has real dimensions
              if (self._swiper && self._swiper.activeIndex === idx) {
                resizeSwiperToSlide(self._swiper, null, true);
              }
            };
            img.addEventListener('load', onImgLoad);
            img.addEventListener('error', function () {
              slide.classList.remove('vp-lb-img--loading');
              img.style.opacity = '1'; // show broken-image icon anyway
            });
          }
          slide.appendChild(img);
        } else if (mode === 'video') {
          var ratio = trigger.getAttribute('data-video-ratio') || '16/9';
          var parts = ratio.split('/');
          var rNum = parts.length === 2
            ? parseFloat(parts[0]) / parseFloat(parts[1])
            : parseFloat(ratio) || 16 / 9;
          slide.dataset.videoUrl = trigger.getAttribute('data-video-url') || '';
          slide.dataset.videoRatio = ratio;
          // Pre-size placeholder so autoHeight measures correct height.
          // min(lb-max-w, lb-max-h * ratio) respects both viewport constraints.
          var placeholder = document.createElement('div');
          placeholder.className = 'vp-lightbox__media';
          placeholder.style.cssText =
            'position:relative;' +
            'width:min(var(--lb-max-w),calc(var(--lb-max-h)*' + rNum + '));' +
            'height:min(var(--lb-max-h),calc(var(--lb-max-w)/' + rNum + '));';
          slide.appendChild(placeholder);
        } else if (mode === 'content') {
          // disable touch swipe so users can scroll content internally
          slide.classList.add('swiper-no-swiping');
          var target = trigger.getAttribute('data-content-target');
          var tpl = target && document.querySelector(target);
          if (tpl) {
            var host = document.createElement('div');
            host.className = 'vp-lightbox__content';
            if (tpl.content) {
              host.appendChild(tpl.content.cloneNode(true));
            } else {
              host.innerHTML = tpl.innerHTML;
            }
            slide.appendChild(host);
          }
        }

        wrapper.appendChild(slide);
      });

      // Compute the rendered width of a slide (including video slides that
      // may not yet have an <iframe>/<video> injected).
      function getSlideWidth(slide) {
        if (!slide) return 0;

        // Video slides: placeholder width is set inline via a CSS min() formula.
        // Before _activateVideoSlide runs, getBoundingClientRect may return 0
        // (the element is off-screen in the swiper track).  Compute directly
        // from the stored ratio and the known CSS variables instead.
        if (slide.dataset.mode === 'video') {
          var ratio = parseFloat(slide.dataset.videoRatio) || (16 / 9);
          if (isNaN(ratio) || ratio <= 0) ratio = 16 / 9;
          // Replicate: min(--lb-max-w, --lb-max-h * ratio)
          var maxW = parseFloat(getComputedStyle(self).getPropertyValue('--lb-max-w')) ||
                     Math.min(960, window.innerWidth * 0.9);
          var maxH = parseFloat(getComputedStyle(self).getPropertyValue('--lb-max-h')) ||
                     (window.innerHeight - 48);
          return Math.round(Math.min(maxW, maxH * ratio));
        }

        var mediaEl = slide.querySelector('.vp-lightbox__media, .vp-lightbox__image, .vp-lightbox__content');
        if (!mediaEl) return 0;
        return Math.round(mediaEl.getBoundingClientRect().width);
      }

      // Compute height of a slide (used instead of autoHeight so we can set
      // it instantly before the transition, preventing animated resize).
      function getSlideHeight(slide) {
        if (!slide) return 0;
        if (slide.dataset.mode === 'video') {
          var ratio = parseFloat(slide.dataset.videoRatio) || (16 / 9);
          if (isNaN(ratio) || ratio <= 0) ratio = 16 / 9;
          var maxW = parseFloat(getComputedStyle(self).getPropertyValue('--lb-max-w')) ||
                     Math.min(960, window.innerWidth * 0.9);
          var maxH = parseFloat(getComputedStyle(self).getPropertyValue('--lb-max-h')) ||
                     (window.innerHeight - 48);
          return Math.round(Math.min(maxH, maxW / ratio));
        }
        // For image/content: read offsetHeight of the actual media so it isn't inflated by Swiper slide stretching
        var mediaEl = slide.querySelector('.vp-lightbox__image, .vp-lightbox__content');
        if (mediaEl && mediaEl.offsetHeight) return mediaEl.offsetHeight;
        return slide.offsetHeight || 0;
      }

      // Instantly resize swiper container (width + height) to match a slide.
      // Pass instant=true to bypass any CSS transitions on the container.
      function resizeSwiperToSlide(swiper, targetSlide, instant) {
        var slide = targetSlide || swiper.slides[swiper.activeIndex];
        if (!slide) return;
        var w = getSlideWidth(slide);
        var h = getSlideHeight(slide);

        if (instant) {
          // Suppress CSS transition so the resize is instant (no visual jump).
          swiperEl.style.transition = 'none';
        }
        if (w > 0) swiperEl.style.width = w + 'px';
        if (h > 0 && swiper.wrapperEl) swiper.wrapperEl.style.height = h + 'px';
        if (instant) {
          // Force layout recalc so transition:none is flushed before we restore.
          void swiperEl.offsetHeight;
          swiperEl.style.transition = '';
        }
        swiper.update();
      }

      swiperEl.appendChild(wrapper);
      if (this.dialog) this.dialog.appendChild(swiperEl);

      // Nav buttons appended to the lightbox host (position:fixed overlay),
      // not inside the dialog — styled inline so they work before CSS build.
      var navBase =
        'position:absolute;top:50%;transform:translateY(-50%);' +
        'z-index:2;width:44px;height:44px;' +
        'display:inline-flex;align-items:center;justify-content:center;' +
        'padding:0;border:none;border-radius:50%;' +
        'background:rgba(255,255,255,0.95);color:#1e1e1e;' +
        'cursor:pointer;box-shadow:0 2px 12px rgba(0,0,0,0.22);' +
        'transition:transform .15s ease,box-shadow .15s ease;';

      var prevBtn = document.createElement('button');
      prevBtn.type = 'button';
      prevBtn.className = 'vp-lightbox__nav-btn';
      prevBtn.setAttribute('aria-label', 'Previous');
      prevBtn.style.cssText = navBase + 'left:16px;';
      prevBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false" width="20" height="20"><path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

      var nextBtn = document.createElement('button');
      nextBtn.type = 'button';
      nextBtn.className = 'vp-lightbox__nav-btn';
      nextBtn.setAttribute('aria-label', 'Next');
      nextBtn.style.cssText = navBase + 'right:16px;';
      nextBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false" width="20" height="20"><path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

      // Hover effect via JS (inline styles bypass CSS build dependency)
      [prevBtn, nextBtn].forEach(function (btn) {
        btn.addEventListener('mouseenter', function () {
          btn.style.transform = 'translateY(-50%) scale(1.08)';
          btn.style.boxShadow = '0 4px 16px rgba(0,0,0,0.28)';
        });
        btn.addEventListener('mouseleave', function () {
          btn.style.transform = 'translateY(-50%)';
          btn.style.boxShadow = '0 2px 12px rgba(0,0,0,0.22)';
        });
      });

      this.appendChild(prevBtn);
      this.appendChild(nextBtn);
      this._navPrev = prevBtn;
      this._navNext = nextBtn;

      this._loadSwiper(function () {
        // Guard: lightbox may have been closed while Swiper was loading
        if (!self._swiperEl) return;

        self._swiper = new window.Swiper(swiperEl, {
          initialSlide: startIndex,
          // autoHeight is intentionally OFF — we manage width+height ourselves
          // by setting them instantly before each transition so neither dimension
          // ever animates during the slide movement (which caused the visible
          // shrink/grow artifacts when switching between image and video slides).
          autoHeight: false,
          keyboard: { enabled: true, onlyInViewport: false },
          navigation: { prevEl: prevBtn, nextEl: nextBtn },
          noSwiping: true,
          noSwipingClass: 'swiper-no-swiping',
          on: {
            afterInit: function (swiper) {
              // instant=true so initial sizing has no transition
              resizeSwiperToSlide(swiper, null, true);
              // Re-snap to the intended slide using the now-real slide width —
              // at init time the container width was still the CSS min()
              // placeholder (0 in practice), so slideTo before this point
              // just moves to the same 0px position as slide 0.
              swiper.slideTo(startIndex, 0, false);
              self._activateVideoSlide(swiper.slides[swiper.activeIndex]);
              self._syncTouchMove(swiper);
            },
            slideChangeTransitionStart: function (swiper) {
              // Instantly set BOTH width and height to the INCOMING slide's
              // dimensions before Swiper begins the horizontal animation.
              // By the time the slide starts moving, the container is already
              // the correct size — so no resize is visible during the transition.
              var incomingSlide = swiper.slides[swiper.activeIndex];
              resizeSwiperToSlide(swiper, incomingSlide, true);
              // Kick off video loading DURING the 300 ms slide animation so
              // the iframe/video has a head-start and the blank gap is hidden.
              if (incomingSlide && incomingSlide.dataset.mode === 'video') {
                var host = incomingSlide.querySelector('.vp-lightbox__media');
                if (host && !host.querySelector('video, iframe')) {
                  self._insertVideoNode(host, incomingSlide.dataset.videoUrl);
                }
              }
              self._deactivateVideoSlide(swiper.slides[swiper.previousIndex]);
            },
            slideChangeTransitionEnd: function (swiper) {
              // Safety resize after the video iframe has been inserted and may
              // have caused a slight layout shift.
              resizeSwiperToSlide(swiper, null, true);
              self._activateVideoSlide(swiper.slides[swiper.activeIndex]);
              // Re-measure now that the iframe/video is in the DOM.
              resizeSwiperToSlide(swiper, null, true);
              self._syncTouchMove(swiper);
            },
          },
        });
      });
    }

    // Disable swipe on content slides so users can scroll vertically inside them
    _syncTouchMove(swiper) {
      var active = swiper.slides[swiper.activeIndex];
      swiper.allowTouchMove = !active || active.dataset.mode !== 'content';
    }

    _loadSwiper(cb) {
      if (window.Swiper) { cb(); return; }
      // Only load Swiper JS. We provide all necessary structural CSS ourselves
      // in vp-lightbox.css to avoid conflicts with page-level Swiper stylesheets.
      var s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/swiper@12/swiper-bundle.min.js';
      s.onload = cb;
      document.head.appendChild(s);
    }

    // Shared helper — creates + inserts the iframe/video node into `host`.
    // The node starts at opacity:0 with a loading spinner shown on the host;
    // it fades in once the browser fires load/canplay.
    _insertVideoNode(host, url) {
      if (!host || !url) return;
      var node;
      var yt = url.match(YT_RE);
      var vm = url.match(VIMEO_RE);
      if (yt) {
        node = document.createElement('iframe');
        node.src = youTubeEmbed(yt[1]);
        node.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');
        node.setAttribute('allowfullscreen', '');
        node.setAttribute('title', 'Video');
      } else if (vm) {
        node = document.createElement('iframe');
        node.src = vimeoEmbed(vm[1]);
        node.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
        node.setAttribute('allowfullscreen', '');
        node.setAttribute('title', 'Video');
      } else {
        node = document.createElement('video');
        node.src = url;
        node.setAttribute('controls', '');
        node.setAttribute('autoplay', '');
        node.setAttribute('playsinline', '');
        node.setAttribute('preload', 'auto');
      }
      // Start invisible; fade in when the media is ready.
      node.style.cssText =
        'position:absolute;inset:0;width:100%;height:100%;border:0;' +
        'opacity:0;transition:opacity 0.25s ease;';
      host.classList.add('vp-lb-media--loading');
      host.appendChild(node);

      function onReady() {
        if (!node.isConnected) return; // navigated away before load
        host.classList.remove('vp-lb-media--loading');
        node.style.opacity = '1';
      }
      node.addEventListener('load', onReady);       // iframe load
      node.addEventListener('canplay', onReady);    // native video ready

      if (node.nodeName === 'VIDEO') {
        var p = node.play();
        if (p && typeof p.catch === 'function') p.catch(function () {});
      }
    }

    _activateVideoSlide(slideEl) {
      if (!slideEl || slideEl.dataset.mode !== 'video') return;
      var host = slideEl.querySelector('.vp-lightbox__media');
      if (!host) return;
      var existing = host.querySelector('video, iframe');
      if (existing) {
        // Pre-loaded during transition — ensure it's visible now.
        existing.style.opacity = '1';
        host.classList.remove('vp-lb-media--loading');
        return;
      }
      // Fallback: not pre-loaded (e.g. initial slide) — insert directly.
      var url = slideEl.dataset.videoUrl;
      if (!url) return;
      this._insertVideoNode(host, url);
    }

    _deactivateVideoSlide(slideEl) {
      if (!slideEl || slideEl.dataset.mode !== 'video') return;
      var host = slideEl.querySelector('.vp-lightbox__media');
      if (!host) return;
      host.querySelectorAll('video').forEach(function (v) { try { v.pause(); } catch (e) {} });
      host.querySelectorAll('iframe').forEach(function (f) { f.src = ''; });
      // Remove only the media element — keep the placeholder div so slide height is preserved
      host.querySelectorAll('video, iframe').forEach(function (el) { el.remove(); });
      host.classList.remove('vp-lb-media--loading');
    }

    close() {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      this.setAttribute('aria-hidden', 'true');
      this.classList.remove('is-loading');
      this.clearActivePanel();
      this.unlockScroll();

      if (typeof window.removeTrapFocus === 'function') {
        window.removeTrapFocus(this.openedBy || undefined);
      } else {
        this._removeLocalTrap();
        if (this.openedBy && typeof this.openedBy.focus === 'function') {
          this.openedBy.focus();
        }
      }
      this.openedBy = null;
      this.activeMode = null;
    }

    showPanel(mode) {
      Object.keys(this.panels).forEach((key) => {
        var p = this.panels[key];
        if (!p) return;
        if (key === mode) p.setAttribute('data-active', '');
        else p.removeAttribute('data-active');
      });
    }

    clearActivePanel() {
      // Destroy Swiper gallery if active
      if (this._swiper) {
        var activeSlide = this._swiper.slides && this._swiper.slides[this._swiper.activeIndex];
        if (activeSlide) this._deactivateVideoSlide(activeSlide);
        try { this._swiper.destroy(true, true); } catch (e) {}
        this._swiper = null;
      }
      if (this._swiperEl) {
        this._swiperEl.remove();
        this._swiperEl = null;
      }
      // Remove nav buttons (appended to lightbox host)
      if (this._navPrev) { this._navPrev.remove(); this._navPrev = null; }
      if (this._navNext) { this._navNext.remove(); this._navNext = null; }

      // Stop & remove single-item video media
      if (this.mediaHost) {
        var vids = this.mediaHost.querySelectorAll('video');
        vids.forEach(function (v) {
          try { v.pause(); } catch (e) {}
        });
        this.mediaHost.innerHTML = '';
      }
      if (typeof window.pauseAllMedia === 'function') {
        try { window.pauseAllMedia(); } catch (e) {}
      }
      // Clear image
      if (this.imageEl) {
        this.imageEl.removeAttribute('src');
        this.imageEl.removeAttribute('alt');
      }
      // Clear content host (cloned markup)
      if (this.contentHost) this.contentHost.innerHTML = '';
      // hide all panels
      Object.keys(this.panels).forEach((key) => {
        if (this.panels[key]) this.panels[key].removeAttribute('data-active');
      });
    }

    renderVideo(url, ratio) {
      if (!url || !this.mediaHost) return;
      var rStr = ratio || '16/9';
      this.mediaHost.style.aspectRatio = rStr;
      var parts = rStr.split('/');
      var rNum = parts.length === 2 ? parseFloat(parts[0]) / parseFloat(parts[1]) : parseFloat(rStr) || (16/9);
      this.mediaHost.style.setProperty('--video-ratio', rNum);
      this.mediaHost.innerHTML = '';
      var node;
      var yt = url.match(YT_RE);
      var vm = url.match(VIMEO_RE);

      if (yt) {
        node = document.createElement('iframe');
        node.src = youTubeEmbed(yt[1]);
        node.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');
        node.setAttribute('allowfullscreen', '');
        node.setAttribute('title', 'Video');
      } else if (vm) {
        node = document.createElement('iframe');
        node.src = vimeoEmbed(vm[1]);
        node.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
        node.setAttribute('allowfullscreen', '');
        node.setAttribute('title', 'Video');
      } else {
        // Treat as direct file (mp4/webm)
        node = document.createElement('video');
        node.src = url;
        node.setAttribute('controls', '');
        node.setAttribute('autoplay', '');
        node.setAttribute('playsinline', '');
        node.setAttribute('preload', 'auto');
      }
      this.mediaHost.appendChild(node);
      if (node.nodeName === 'VIDEO') {
        var p = node.play();
        if (p && typeof p.catch === 'function') p.catch(function () {});
      }
    }

    renderImage(src, alt) {
      if (!src || !this.imageEl) return;
      this.classList.add('is-loading');
      this.imageEl.onload = this.imageEl.onerror = () => this.classList.remove('is-loading');
      this.imageEl.setAttribute('src', src);
      this.imageEl.setAttribute('alt', alt || '');
      if (this.imageEl.complete && this.imageEl.naturalHeight > 0) {
        this.classList.remove('is-loading');
      }
    }

    renderContent(targetSelector) {
      if (!targetSelector || !this.contentHost) return;
      var tpl = document.querySelector(targetSelector);
      this.contentHost.innerHTML = '';
      if (!tpl) return;
      if (tpl.content) {
        this.contentHost.appendChild(tpl.content.cloneNode(true));
      } else {
        this.contentHost.innerHTML = tpl.innerHTML;
      }
      // reset internal scroll position
      if (this.panels.content) this.panels.content.scrollTop = 0;
    }

    /* ---- scroll lock (preserves scrollbar width to avoid layout shift) ---- */
    lockScroll() {
      var sw = window.innerWidth - document.documentElement.clientWidth;
      if (sw > 0) document.documentElement.style.setProperty('--vp-scrollbar-w', sw + 'px');
      document.documentElement.classList.add('vp-lightbox-open');
      if (sw > 0) document.body.style.paddingRight = sw + 'px';
    }

    unlockScroll() {
      document.documentElement.classList.remove('vp-lightbox-open');
      document.body.style.paddingRight = '';
    }

    /* ---- minimal local focus trap fallback (only if theme globals absent) ---- */
    _localTrap() {
      this._removeLocalTrap();
      this._trapHandler = (e) => {
        if (e.key !== 'Tab' && e.code !== 'Tab') return;
        var f = getFocusable(this);
        if (!f.length) return;
        var first = f[0];
        var last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      };
      this.addEventListener('keydown', this._trapHandler);
    }

    _removeLocalTrap() {
      if (this._trapHandler) {
        this.removeEventListener('keydown', this._trapHandler);
        this._trapHandler = null;
      }
    }
  }

  if (!customElements.get('vp-lightbox')) {
    customElements.define('vp-lightbox', VpLightbox);
  }
})();
